import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ClockIcon, DollarSignIcon, GraduationCapIcon, ShieldCheckIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { LegalFooter } from '../components/LegalFooter';
import { buildFindDegreesClickout } from '../services/mediaMatchers/clickout';
import { getRelatedLevels, getRelatedPrograms, type DegreeProgramCombo } from '../utils/degreeSlugs';
interface DegreeProgramLandingPageProps {
  combo: DegreeProgramCombo;
}

/**
 * Generated landing page for one degree level × program combination.
 *
 * The copy is assembled from the combination itself rather than hand-written,
 * so every program has a page without claiming specifics — no invented tuition
 * figures, salaries, school names, or program lengths.
 */
export function DegreeProgramLandingPage({
  combo
}: DegreeProgramLandingPageProps) {
  const location = useLocation();
  usePageMeta(`Online ${combo.title} Programs | Online College Guide`, `Compare accredited online ${combo.programLabel} programs. Get matched free by career goals, schedule, and budget.`);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [combo.slug]);
  const ctaHref = buildFindDegreesClickout(location.search, {
    degreeInterest: combo.degreeInterest,
    areaStudy: combo.areaStudy,
    mProgram: combo.mProgram
  });
  const relatedLevels = getRelatedLevels(combo);
  const relatedPrograms = getRelatedPrograms(combo);
  const reasons = [{
    icon: ShieldCheckIcon,
    title: 'Accredited schools only',
    body: `Every ${combo.programLabel} program we match you with comes from an accredited institution, so your credits and credential hold their value.`
  }, {
    icon: ClockIcon,
    title: 'Built around your schedule',
    body: 'Online coursework lets you keep working while you study, with start dates throughout the year rather than one intake each fall.'
  }, {
    icon: DollarSignIcon,
    title: 'Aid you may qualify for',
    body: 'Accredited schools eligible for federal aid can accept the FAFSA. Ask each school what applies to your situation once you are matched.'
  }];
  return <main className="bg-white" data-id="element-1405" data-mp="yg9ymw7b4kox">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-100" data-id="element-1406" data-mp="tkb5kvb1aei6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-1407" data-mp="5tox52x4as7n">
          <Link to="/" className="flex items-center" data-id="element-1408" data-mp="um5ck65eihm">
            <img src="https://cdn.magicpatterns.com/uploads/hM6pwxGTxH7R3Ug6xJ6sm8/Horizontal_OCG_logo.png" alt="Online College Guide - Your Path Forward" className="h-8 md:h-10 w-auto object-contain" data-id="element-1409" data-mp="w5ja44c0hg8" />
          </Link>
          <div className="hidden sm:flex items-center text-xs font-semibold text-gray-500 tracking-wider uppercase" data-id="element-1410" data-mp="3j7s97th356au">
            <ShieldCheckIcon className="w-4 h-4 mr-1.5 text-green-600" data-id="element-1411" data-mp="3e8ph19m7euka" />
            Trusted by 2M+ Students
          </div>
        </div>
      </div>

      {/* Hero */}
      <header className="hero-bg" data-id="element-1412" data-mp="1yiokoo6j1ogn">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20" data-id="element-1413" data-mp="1yjr8gn8jzasc">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-1414" data-mp="1jubllic57phx">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-1415" data-mp="1osd3hzxu1irt">
              <li data-id="element-1416" data-mp="1oqabjad4dfkg">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-1417" data-mp="2ci7eebylcx9f">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1418" data-mp="2hfpqucbthum6">/</li>
              <li data-id="element-1419" data-mp="2heodg17bww1a">
                <Link to="/find-degrees" className="hover:text-white transition-colors" data-id="element-1420" data-mp="23hj9c6r6e78o">
                  Find Degrees
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1421" data-mp="s1unxjtklg9z">/</li>
              <li className="text-white/90" data-id="element-1422" data-mp="s0t16i36z5cf">{combo.areaLabel}</li>
            </ol>
          </nav>

          <div className="grid lg:grid-cols-3 gap-10 items-start" data-id="element-1423" data-mp="w50tsqcrt5fx">
            <div className="order-2 lg:order-1 lg:col-span-2" data-id="element-1424" data-mp="1cbvterwxrevl">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-gold mb-4" data-id="element-1425" data-mp="17k3zc7ekmr0p">
                {combo.degreeLabel} · {combo.areaLabel}
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5" data-id="element-1426" data-mp="12iy7ebsqj7s8">
                Online {combo.title} Programs
              </h1>
              <p className="text-lg text-white/80 leading-relaxed max-w-2xl mb-8" data-id="element-1427" data-mp="1v8dj73s854p7">
                Compare accredited online {combo.programLabel.toLowerCase()}{' '}
                programs at the {combo.degreeLabel.toLowerCase()} level. Tell us
                your goals and we'll connect you with schools that fit your
                schedule and budget — free, with no obligation.
              </p>
              <ul className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/80" data-id="element-1428" data-mp="9x50pr4bj906">
                {['Accredited', 'Flexible online study', 'No obligation'].map(item => <li key={item} className="inline-flex items-center gap-2" data-id="element-1429" data-mp="2hjeua67lqixi">
                      <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-1430" data-mp="2lixj3d2jne5m" />
                      {item}
                    </li>)}
              </ul>
            </div>

            {/* CTA card */}
            <div className="order-1 lg:order-2 bg-white rounded-2xl shadow-2xl ring-4 ring-white/20 p-8 sm:p-9 text-center" data-id="element-1431" data-mp="307tot3h4of32">
              <GraduationCapIcon className="w-11 h-11 text-navy mx-auto mb-5" aria-hidden="true" data-id="element-1432" data-mp="359sq87tb51xd" />
              <h2 className="text-2xl font-extrabold text-navy leading-tight mb-3" data-id="element-1433" data-mp="3e8mijlzlv654">
                Find {combo.programLabel} Colleges
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-7" data-id="element-1434" data-mp="1exd498aakt7h">
                We'll show you accredited schools offering{' '}
                {combo.programLabel.toLowerCase()} programs.
              </p>
              <a href={ctaHref} className="group w-full inline-flex items-center justify-center gap-2 bg-cta hover:bg-cta-dark text-white font-extrabold py-5 px-6 rounded-xl text-lg shadow-lg hover:shadow-xl transition-[background-color,box-shadow] duration-200 focus:outline-none focus:ring-4 focus:ring-cta/40" data-id="element-1435" data-mp="2i6629ywbgjqa">
                Get Matched Free
                <ArrowRightIcon className="w-6 h-6 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-1436" data-mp="3151u53kpcm0y" />
              </a>
              <p className="mt-5 text-sm text-gray-500" data-id="element-1437" data-mp="3autpi00mlj6h">
                Takes about 2 minutes
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Why online */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20" data-id="element-1438" data-mp="3jvjkx2lz57jo">
        <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-3 text-center" data-id="element-1439" data-mp="3jtf8fwa0f07s">
          Why study {combo.programLabel.toLowerCase()} online?
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed text-center" data-id="element-1440" data-mp="c9z1nr0tif95">
          An online {combo.degreeLabel.toLowerCase()} lets you build
          credentials in {combo.areaLabel.toLowerCase()} without leaving your
          job. What matters to employers is the school's accreditation and the
          skills you gain, not whether you attended in person.
        </p>
        <div className="grid md:grid-cols-3 gap-8" data-id="element-1441" data-mp="3pilqf3ptth71">
          {reasons.map(reason => <div key={reason.title} className="flex flex-col items-center text-center" data-id="element-1442" data-mp="3atfhgo16scly">
              <reason.icon className="w-6 h-6 text-crimson mb-4" aria-hidden="true" data-id="element-1443" data-mp="3fr7j49rmvd3c" />
              <h3 className="text-base font-bold text-navy mb-2" data-id="element-1444" data-mp="31t96p9wvhqqh">
                {reason.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1445" data-mp="35srrm5op21tb">
                {reason.body}
              </p>
            </div>)}
        </div>
      </section>

      {/* Related */}
      {(relatedLevels.length > 0 || relatedPrograms.length > 0) && <section className="bg-light border-y border-gray-200" data-id="element-1446" data-mp="3c9uxff8l3m5k">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-2 gap-12" data-id="element-1447" data-mp="37btf4wizfqv5">
            {relatedLevels.length > 0 && <div data-id="element-1448" data-mp="33azq1ztt060o">
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-1449" data-mp="33biz0r1h7k2k">
                  {combo.programLabel} at other levels
                </h2>
                <ul className="space-y-2" data-id="element-1450" data-mp="2tlg4unimc02n">
                  {relatedLevels.map(item => <li key={item.slug} data-id="element-1451" data-mp="2omczt32n1tuo">
                      <Link to={`/find-degrees/${item.slug}`} className="inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-crimson transition-colors" data-id="element-1452" data-mp="2oj7e7xt7xa3p">
                        {item.degreeLabel} in {item.programLabel}
                        <ArrowRightIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1453" data-mp="v4xnyks9j4oq" />
                      </Link>
                    </li>)}
                </ul>
              </div>}
            {relatedPrograms.length > 0 && <div data-id="element-1454" data-mp="bfdf5ac6dwgp">
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-1455" data-mp="bfwvwnq3aet5">
                  Other {combo.areaLabel.toLowerCase()} programs
                </h2>
                <ul className="space-y-2" data-id="element-1456" data-mp="lci0x923zpwz">
                  {relatedPrograms.map(item => <li key={item.slug} data-id="element-1457" data-mp="q3rpe6bx8n7a">
                      <Link to={`/find-degrees/${item.slug}`} className="inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-crimson transition-colors" data-id="element-1458" data-mp="q4t3yks5brtr">
                        {item.programLabel}
                        <ArrowRightIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1459" data-mp="22iz95s8o2tib" />
                      </Link>
                    </li>)}
                </ul>
              </div>}
          </div>
        </section>}

      {/* Closing CTA */}
      <section className="bg-navy" data-id="element-1460" data-mp="1jt9ed54wm7qe">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-id="element-1461" data-mp="1juc4vbybe85s">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" data-id="element-1462" data-mp="1ot5nfisgkpct">
            Ready to compare {combo.programLabel.toLowerCase()} programs?
          </h2>
          <p className="text-white/80 leading-relaxed mb-8" data-id="element-1463" data-mp="1yhxabcinwjgv">
            Answer a few questions and we'll match you with accredited schools
            offering online {combo.degreeLabel.toLowerCase()} programs. Free for
            students, with no obligation to enroll.
          </p>
          <a href={ctaHref} className="group inline-flex items-center justify-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-4 px-10 rounded-lg text-base transition-colors duration-200 shadow-lg" data-id="element-1464" data-mp="15z9uocmfpwn6">
            Get Matched Free
            <ArrowRightIcon className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-1465" data-mp="3a9d2u5pkzrrq" />
          </a>
          <p className="mt-5 text-xs text-white/50 uppercase tracking-widest" data-id="element-1466" data-mp="30dtjeis7eljv">
            Free · No obligation · Takes 2 minutes
          </p>
        </div>
      </section>

      <LegalFooter data-id="element-1467" data-mp="2rk6oeuvtlmbq" />
    </main>;
}